/**
 * SKY EXTENSION for the Main Image (2026-09-18, Jack: "pull the sky set up from the html
 * version, it's fully built, leverage the entire set up") — DEV / QA ONLY.
 *
 * The maths is not here. It is the monolith's baker, vendored verbatim in
 * `vendor/ovSky.js` (index.html L14830–16971) — the approved sky set, the cumulus and 47-crop
 * libraries, colour-matched plates, the seam feather, smart stretch, fade — every one of them a
 * measured decision recorded in `_changesets/2026-08-26-overlay-revive-milestone2.md`. This
 * file only builds the model that baker expects and reads the result back.
 *
 * What the requester sees: a slider on the Main Image card, "Extend sky" 0–45%. The photo's
 * own sky is used first; only a genuine shortfall is synthesised (Auto), unless "Always" is on.
 * The bake lands in the block's image field as a `data:` URL, exactly like the slideshow GIF
 * and the overlay-text hero — and like them it is hosted on SFMC's CDN at submit
 * (services/hostImages.ts), so the row and the email carry a real URL. The pristine photo is
 * kept beside it (`<key>__ovbase`, the monolith's own contract) so every re-bake starts from
 * the original and moving the slider back to 0 restores it.
 */
import { __ovRecompose, __ovPhotoKind, __ovKindDefaults, type OvModel } from "../vendor/ovSky";
import { nonProdHost } from "./namedDrafts";

/** non-prod only — drafts went to production on 2026-09-23, the overlay editor did not */
export const skyExtendEnabled = nonProdHost;

export const SKY_MAX = 45;
export const SKY_DEBOUNCE_MS = 350;   /* the monolith's __ovSchedule */

export interface SkySpec {
  /** 0–45, % of the final frame that is sky */
  sky: number;
  /** "Always": paint the full band even when the photo already has enough sky */
  force?: boolean;
  /** a specific approved sky (url from /sky-set); "" = Auto */
  pick?: string;
  /** fade-to-white at the bottom, 0–40 (the Milestone 2 layout); default 0 */
  fade?: number;
}

export interface SkyBake { dataUrl: string; skyPx: number; frameH: number }

/** the hero def's overlay fields (index.html L8150) with the geometry the caller asked for — text and effects OFF */
export function skyModel(base: string, spec: SkySpec): OvModel {
  const sky = Math.max(0, Math.min(SKY_MAX, Math.round(+spec.sky || 0)));
  return {
    data: {
      src: base,
      alt: "Hero",
      ovOn: "0", ovKicker: "", ovHead: "", ovSub: "", ovCol: "#ffffff", ovAlign: "center", ovV: "middle",
      ovSize: "40", ovScrim: "0", ovFrame: "0",
      ovSky: String(sky),
      ovFade: String(Math.max(0, Math.min(40, Math.round(+(spec.fade || 0))))),
      ovSkyPick: spec.pick || "",
      ovBoxH: "", ovBlend: "24",
      ovForce: spec.force ? "1" : "0",
      ovSkyTouched: spec.force ? "1" : "0",
      ovCta: "", ovBadges: "",
      src__ovfx: "none",
    },
  };
}

export const photoKind = __ovPhotoKind;
export const kindDefaults = __ovKindDefaults;

/** bake the sky band onto `base`; rejects with the baker's own reason ("cors" | "photo" | "no photo") */
export function bakeSky(base: string, spec: SkySpec): Promise<SkyBake> {
  return new Promise((res, rej) => {
    const m = skyModel(base, spec);
    __ovRecompose(m, "src", (out, err) => {
      if (!out) { rej(new Error(err || "bake failed")); return; }
      res({ dataUrl: out, skyPx: Number(m.__skyPx || 0), frameH: Number(m.__frameH || 0) });
    });
  });
}

/** what the slider means, for the card's one status line */
export function skyStatus(b: SkyBake | null, spec: SkySpec): string {
  if (!spec.sky) return "";
  if (!b) return "Extending the sky…";
  if (b.skyPx <= 0) return "This photo already has enough sky — nothing added." + (spec.force ? "" : " Turn on Always to add the band anyway.");
  return "Sky extended by " + b.skyPx + "px — frame " + b.frameH + "px tall.";
}
